<template>
  <el-input
    class="el-date-editor kye-date-picker2-editor"
    :class="'el-date-editor--' + type"
    :readonly="readonlySingle"
    :disabled="pickerDisabled"
    :size="pickerSize"
    :name="name"
    v-bind="firstInputId"
    v-if="!ranged"
    v-clickoutside="handleClose"
    :placeholder="currentPlaceholder"
    @focus="handleFocus"
    @keydown.native="dateHandleKeydown"
    :value="isMultiple ? '' : templateDisplayValue"
    @input="value => userInput = value"
    @mouseenter.native="handleMouseEnter"
    @mouseleave.native="showClose = false"
    :validateEvent="false"
    ref="reference">
    <div
      slot="prefix"
      v-if="isMultiple && multipInputPrefixConfig.lastDate"
      class="months-prefix-wrap"
    >
      <span class="flex" @click="handleFocus">
        <span class="el-tag el-tag--info el-tag--mini">
          <span class="el-select__tags-text">{{multipInputPrefixConfig.lastDate}}</span>
          <i class="el-tag__close el-icon-close" @click.stop="handleDeleteMonths"></i>
        </span>
        <span
          class="el-tag el-tag--info el-tag--mini ml-6"
          v-if="multipInputPrefixConfig.leng> 1"
        >
          <span class="el-select__tags-text">+{{multipInputPrefixConfig.num}}</span>
        </span>
      </span>
    </div>
    <i
      v-else
      slot="prefix"
      class="el-input__icon"
      :class="triggerClass"
      @click="handleFocus">
    </i>
    <i slot="suffix"
      class="el-input__icon"
      @click="handleClickIcon"
      :class="[showClose ? '' + clearIcon : '']"
      v-if="haveTrigger">
    </i>
    <i slot="suffix"
      class="kye-date-picker2-icon-date"
      v-if="!showClose">
    </i>
  </el-input>

  <div
    class="el-date-editor el-range-editor el-input__inner kye-picker2-range-editor"
    :class="[
      'el-date-editor--' + type,
      pickerSize ? `el-range-editor--${ pickerSize }` : '',
      pickerDisabled ? 'is-disabled' : '',
      pickerVisible ? 'is-active' : '',
    ]"
    @click="handleRangeClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="showClose = false"
    @keydown="handleKeydown"
    ref="reference"
    v-clickoutside="handleClose"
    v-else>
    <!-- <i :class="['el-input__icon', 'el-range__icon', triggerClass]"></i> -->
    <div class="kye-picker2-range-editor-input-wrap">
      <input
        autocomplete="off"
        :placeholder="currentPlaceholder && currentPlaceholder[0]"
        :value="templateDisplayValue && templateDisplayValue[0]"
        :disabled="pickerDisabled"
        v-bind="firstInputId"
        :readonly="readonlyRanged"
        :name="name && name[0]"
        @input="handleStartInput"
        @focus="handleFocus($event, 0)"
        class="el-range-input kye-picker2-range-editor-input-start"
        :class="[
          startInputBgVisible ? 'kye-picker2-range-editor-input-bg-start' : ''
        ]"
        @keydown="dateRangeHandleKeydown($event, 0)"
        ref="referenceStart">
      <slot name="range-separator">
        <span class="range-separator">{{ rangeSeparator }}</span>
      </slot>
      <input
        autocomplete="off"
        :placeholder="currentPlaceholder && currentPlaceholder[1]"
        :value="templateDisplayValue && templateDisplayValue[1]"
        :disabled="pickerDisabled"
        v-bind="secondInputId"
        :readonly="readonlyRanged"
        :name="name && name[1]"
        @input="handleEndInput"
        @focus="handleFocus($event, 1)"
        class="el-range-input kye-picker2-range-editor-input-end"
        :class="[
          endInputBgVisible ? 'kye-picker2-range-editor-input-bg-end' : ''
        ]"
        @keydown="dateRangeHandleKeydown($event, 1)"
        ref="referenceEnd">
    </div>
    <div class="kye-picker2-range-editor-icon-wrap">
      <i
        @click="handleClickIcon"
        v-if="haveTrigger"
        :class="[showClose ? '' + clearIcon : '']"
        class="el-input__icon close-icon">
      </i>
      <i slot="suffix"
        class="icon-date"
        v-if="!showClose">
      </i>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import Clickoutside from 'element-ui/lib/utils/clickoutside'
import {
  PLACEMENT_MAP,
  HAVE_TRIGGER_TYPES,
  DEFAULT_FORMATS,
  TYPE_VALUE_RESOLVER_MAP,
  parseAsFormatAndType,
  formatAsFormatAndType,
  valueEquals,
  isDateObject,
  parseInputDate,
  correctDatesOrder,
  dateFormatCallback,
  isDate,
  tailWeekCheck
} from './utils'
import Emitter from 'element-ui/lib/mixins/emitter' // 向form-item 广播对应的校验方法，使其form中的校验方法生效。比如trigger中配置了blur等
import { NewPopper } from './mixins/popper' // 操作面板为气泡的内容的气泡混入代码。使用到vue-popper
import { PICKER_PROPS } from './props/picker' // 当前的props
import './style/index.scss' // 现在UI的样式表
import moment from 'moment'

export default {
  mixins: [Emitter, NewPopper],

  inject: {
    elForm: {
      default: ''
    },
    elFormItem: {
      default: ''
    }
  },

  props: PICKER_PROPS,

  directives: { Clickoutside },

  data() {
    return {
      pickerVisible: false,
      showClose: false,
      userInput: null,
      userHover: null,
      valueOnOpen: null, // 选择器打开时的值，用于确定是否发出更改
      unwatchPickerOptions: null,
      activeInputIndex: 0,
      startInputBgVisible: false,
      endInputBgVisible: false,
    }
  },

  watch: {
    pickerVisible(val) {
      if (this.$props.readonly || this.pickerDisabled) return
      if (val) {
        this.showPicker()
        this.valueOnOpen = Array.isArray(this.value) ? [...this.value] : this.value
      } else {
        this.hidePicker()
        this.emitChange(this.value)
        this.userInput = null
        if (this.isDoublePanel) {
          this.userHover = null
        }
        if (this.$props.validateEvent) {
          this.dispatch('ElFormItem', 'el.form.blur')
        }
        this.$emit('blur', this)
        this.blur()
      }
      // 检查datetimedate是否双边
      val === false && this.$props.singleValue === false && setTimeout(() => {
        if (this.pickerVisible === false) {
          this.userHover = null
        }
      }, 300)
    },
    // parsedValue: {
    //   immediate: true,
    //   handler(val) {
    //     if (this.picker && !this.userHover) {
    //       this.picker.value = val
    //       console.log('更新1111', val.length)
    //     }
    //   }
    // },
    defaultValue(val) {
      // 注意：最终应该移动到jsx样式的选择器+面板吗？
      if (this.picker) {
        this.picker.defaultValue = val
      }
    },
    value(val, oldVal) {
      if (!valueEquals(val, oldVal) && !this.pickerVisible && this.$props.validateEvent) {
        this.dispatch('ElFormItem', 'el.form.change', val)
      }
      // tip: 放入到这里的目的是为了解决日期hover重复设置问题
      this.$nextTick(() => {
        if (this.picker && !this.userHover) {
          this.picker.value = this.parsedValue
        }
      })
    }
  },

  computed: {
    ranged() {
      return this.$props.type.indexOf('range') > -1
    },

    isDateTimeRange() {
      return this.$props.type === 'datetimerange'
    },

    reference() {
      const reference = this.$refs.reference
      return reference.$el || reference
    },

    refInput() {
      if (this.reference) {
        return [].slice.call(this.reference.querySelectorAll('input'))
      }
      return []
    },

    valueIsEmpty() {
      const val = this.value
      if (Array.isArray(val) && val.filter(Boolean).length === 0) {
        return false
      } else {
        if (val) {
          return false
        }
      }
      return true
    },

    triggerClass() {
      return this.$props.prefixIcon || (this.$props.type.indexOf('time') !== -1 ? 'el-icon-time' : 'el-icon-date')
    },

    selectionMode() {
      if (this.$props.type === 'week') {
        return 'week'
      } else if (this.$props.type === 'month') {
        return 'month'
      } else if (this.$props.type === 'year') {
        return 'year'
      } else if (this.$props.type === 'dates') {
        return 'dates'
      } else if (this.$props.type === 'months') {
        return 'months'
      } else if (this.$props.type === 'years') {
        return 'years'
      }

      return 'day'
    },

    haveTrigger() {
      if (typeof this.showTrigger !== 'undefined') {
        return this.showTrigger
      }
      return HAVE_TRIGGER_TYPES.indexOf(this.$props.type) !== -1
    },

    displayValue() {
      let formattedValue = formatAsFormatAndType(this.parsedValue, this.$props.format, this.$props.type, this.$props.rangeSeparator)
      // 这里为了区分跨年周场景，value要拿周的第一天，格式化要拿下一年，比如2024-12-30是2025的第一周
      if (this.$props.type === 'week') {
        formattedValue = tailWeekCheck(this.parsedValue, DEFAULT_FORMATS.week)
      }
      if (Array.isArray(this.userInput)) {
        return [
          this.userInput[0] || (formattedValue && formattedValue[0]) || '',
          this.userInput[1] || (formattedValue && formattedValue[1]) || ''
        ]
      } else if (this.userInput !== null) {
        return this.userInput
      } else if (formattedValue) {
        return (this.$props.type === 'dates' || this.$props.type === 'years')
          ? formattedValue.join(', ')
          : formattedValue
      } else {
        return ''
      }
    },
    templateDisplayValue () {
      if (this.$props.formatter && typeof this.$props.formatter === 'function') {
        return this.$props.formatter({
          displayValue: this.displayValue,
          parsedValue: this.parsedValue,
        })
      }
      return this.displayValue
    },

    multipInputPrefixConfig () {
      const data = String(this.displayValue).split(',').reduce((acc, val) => {
        const v = String(val).trim()
        if (v) {
          acc.push(val)
        }
        return acc
      }, [])
      const leng = data.length
      const lastIndex = leng - 1
      const lastDate = data[lastIndex]
      return {
        lastDate,
        leng,
        num: lastIndex
      }
    },

    isMultiple () {
      return this.$props.type === 'months' || this.$props.type === 'dates'
    },
    isDates () {
      return this.$props.type === 'dates'
    },

    parsedValue() {
      const value = this.isDates ? this.value : (this.userHover || this.value)
      if (!value) return value
      if (['time-select', 'monthrange'].includes(this.$props.type)) return value // 时间选择不需要解析，这可能会在下一个主要版本中发生变化

      const valueIsDateObject = isDateObject(value) || (Array.isArray(value) && value[this.isDateTimeRange ? 'some' : 'every'](isDateObject))
      if (valueIsDateObject) {
        return value
      }
      if (this.$props.valueFormat) {
        return parseAsFormatAndType(value, this.$props.valueFormat, this.$props.type, this.$props.rangeSeparator) || value
      }

      // 注意：处理常见但不正确的用法，应在下一个主要版本中删除
      // 用户可以提供没有值格式的字符串/时间戳，将其强制转换为日期（或日期数组）
      return Array.isArray(value) ? value.map(val => val ? new Date(val) : '') : new Date(value)
    },

    _elFormItemSize() {
      return (this.elFormItem || {}).elFormItemSize
    },

    pickerSize() {
      return this.$props.size || this._elFormItemSize || (this.$ELEMENT || {}).size
    },

    pickerDisabled() {
      return this.$props.disabled || (this.elForm || {}).disabled
    },

    firstInputId() {
      const obj = {}
      let id
      if (this.ranged) {
        id = this.$props.id && this.$props.id[0]
      } else {
        id = this.$props.id
      }
      if (id) obj.id = id
      return obj
    },

    secondInputId() {
      const obj = {}
      let id
      if (this.ranged) {
        id = this.$props.id && this.$props.id[1]
      }
      if (id) obj.id = id
      return obj
    },
    currentPlaceholder () {
      const map = {
        'date': '选择日期',
        'datetime': '选择日期时间',
        'daterange': ['开始日期', '结束日期'],
        'datetimerange': ['开始时间', '结束时间'],
        'week': '选择周',
        'month': '选择月份',
        'months': '选择月份',
        'monthrange': ['开始月份', '结束月份'],
      }
      // 多选月是盖上方实现的
      if (this.isMultiple && this.multipInputPrefixConfig.lastDate) {
        return ''
      }
      const val = map[this.$props.type]
      if (!this.ranged) {
        return this.$props.placeholder || val
      } else {
        return [this.$props.startPlaceholder || val[0], this.$props.endPlaceholder || val[1]]
      }
    },
    readonlySingle () {
      return !this.$props.editable || this.$props.readonly || this.$props.type === 'dates' || this.$props.type === 'week' || this.$props.type === 'years' || this.$props.type === 'months'
    },
    readonlyRanged () {
      return !this.$props.editable || this.$props.readonly
    },
    isDoublePanel () {
      return ['daterange', 'monthrange'].includes(this.$props.type)
    }
  },

  created() {
    // vue-popper
    this.popperOptions = {
      boundariesPadding: 0,
      gpuAcceleration: false
    }
    this.placement = PLACEMENT_MAP[this.$props.align] || PLACEMENT_MAP.left

    this.$on('fieldReset', this.handleFieldReset)
  },

  methods: {
    focus() {
      if (!this.ranged) {
        this.$refs.reference.focus()
      } else {
        this.handleFocus()
      }
    },

    blur() {
      this.refInput.forEach(input => input.blur())
    },

    // {parse，formatTo}值交易将组件值与内部日期映射
    parseValue(value) {
      const isSingleValue = this.singleValue
      const isParsed = isDateObject(value) || (Array.isArray(value) && value[isSingleValue ? 'some' : 'every'](isDateObject))
      if (this.$props.valueFormat && !isParsed) {
        return parseAsFormatAndType(value, this.$props.valueFormat, this.$props.type, this.$props.rangeSeparator) || value
      } else {
        return value
      }
    },

    formatToValue(date) {
      const isSingleValue = this.singleValue
      const isFormattable = isDateObject(date) || (Array.isArray(date) && date[isSingleValue ? 'some' : 'every'](isDateObject))
      if (this.$props.valueFormat && isFormattable) {
        return formatAsFormatAndType(date, this.$props.valueFormat, this.$props.type, this.$props.rangeSeparator)
      } else {
        return date
      }
    },

    // {parse，formatTo}字符串处理用户输入
    parseString(value) {
      const type = Array.isArray(value) ? this.$props.type : this.$props.type.replace('range', '')
      return parseAsFormatAndType(value, this.$props.format, type)
    },

    formatToString(value) {
      const type = Array.isArray(value) ? this.$props.type : this.$props.type.replace('range', '')
      return formatAsFormatAndType(value, this.$props.format, type)
    },

    handleMouseEnter() {
      if (this.$props.readonly || this.pickerDisabled) return
      if (!this.valueIsEmpty && this.$props.clearable) {
        this.showClose = true
      }
    },

    handleChange() {
      if (this.userInput) {
        const value = this.parseString(this.displayValue)
        if (value) {
          this.picker.value = value
          if (this.isValidValue(value)) {
            this.emitInput(value)
            this.userInput = null
          }
        }
      }
      if (this.userInput === '') {
        this.emitInput(null)
        this.emitChange(null)
        this.userInput = null
      }
    },

    handleStartInput(event) {
      if (this.userInput) {
        this.userInput = [event.target.value, this.userInput[1]]
      } else {
        this.userInput = [event.target.value, null]
      }
    },

    handleEndInput(event) {
      if (this.userInput) {
        this.userInput = [this.userInput[0], event.target.value]
      } else {
        this.userInput = [null, event.target.value]
      }
    },

    handleClickIcon(event) {
      if (this.$props.readonly || this.pickerDisabled) return
      if (this.showClose) {
        this.valueOnOpen = this.value
        event.stopPropagation()
        this.emitInput(null)
        this.emitChange(null)
        this.showClose = false
        this.userHover = null
        if (this.picker && typeof this.picker.handleClear === 'function') {
          this.picker.handleClear()
        }
        this.$emit('clear')
      } else {
        this.pickerVisible = !this.pickerVisible
      }
    },

    handleClose() {
      if (!this.pickerVisible) return
      this.pickerVisible = false

      if (this.$props.type === 'dates' || this.$props.type === 'years' || this.$props.type === 'months') {
        // 恢复到原来的值
        const oldValue = parseAsFormatAndType(this.valueOnOpen, this.$props.valueFormat, this.$props.type, this.$props.rangeSeparator) || this.valueOnOpen
        this.emitInput(oldValue)
      }
      this.changStratEendInputBgVisible(false)
    },

    handleFieldReset(initialValue) {
      this.userInput = initialValue === '' ? null : initialValue
    },

    handleFocus(_, activeInputIndex) {
      const type = this.$props.type
      this.activeInputIndex = activeInputIndex
      if (this.picker) {
        this.picker.activeInputIndex = activeInputIndex
      }
      if (HAVE_TRIGGER_TYPES.indexOf(type) !== -1 && !this.pickerVisible) {
        this.pickerVisible = true
      }
      if (typeof activeInputIndex === 'number') {
        this.changStratEendInputBgVisible(false, activeInputIndex === 0 ? 1 : 0)
        this.changStratEendInputBgVisible(true, activeInputIndex === 0 ? 0 : 1)
      }
      this.$emit('focus', this)
    },

    handleKeydown(event) {
      const keyCode = event.keyCode

      // Delete
      if ((keyCode === 8 || keyCode === 46) && this.disableDelete) {
        event.stopPropagation()
        event.preventDefault()
        return
      }

      // ESC
      if (keyCode === 27) {
        this.pickerVisible = false
        event.stopPropagation()
        return
      }

      // Tab
      if (keyCode === 9) {
        if (!this.ranged) {
          this.handleChange()
          this.pickerVisible = this.picker.visible = false
          this.blur()
          event.stopPropagation()
        } else {
          // user may change focus between two input
          setTimeout(() => {
            if (this.refInput.indexOf(document.activeElement) === -1) {
              this.pickerVisible = false
              this.blur()
              event.stopPropagation()
            }
          }, 0)
        }
      }

      // if user is typing, do not let picker handle key input
      // if (this.userInput) {
      //   event.stopPropagation()
      //   return
      // }

      // delegate other keys to panel
      // if (this.picker && this.picker.handleKeydown) {
      //   this.picker.handleKeydown(event)
      // }
    },
    getCurrentPickerYear (isLeft) {
      let year = new Date().getFullYear()
      if (this.ranged) {
        const rangeYear = this.picker[isLeft ? 'leftYear' : 'rightYear']
        if (rangeYear) {
          year = rangeYear
        }
      } else {
        const pickerYear = this.picker.year
        if (pickerYear) {
          year = pickerYear
        }
      }
      return String(year)
    },
    // handleErrorPopoverEvent (visible, referenceElm) {
    //   if (this.$refs.pickerPopper) {
    //     this.$refs.pickerPopper.setDifferentErrorTips(this.$props.type, this.$props.isParseInputYear)
    //     this.$refs.pickerPopper.handleErrorPopoverEvent(visible, referenceElm)
    //     if (this.ranged) {
    //       this.$refs.pickerPopper.forceUpdatePopper()
    //     }
    //   }
    // },
    isOptionDisabledDate (time) {
      const disabledDate = this.$props.pickerOptions && this.$props.pickerOptions.disabledDate
      if (!disabledDate || typeof disabledDate !== 'function') {
        return false
      }
      const disabled = disabledDate(isDate(time) ? time : moment(time).toDate())
      return !!disabled
    },
    emitInputEnter (...args) {
      this.$emit('input-enter', ...args)
    },
    dateHandleKeydown (event) {
      // 单面板的键盘事件
      const keyCode = event.keyCode
      if (keyCode === 13) {
        if (this.pickerDisabled || this.readonlySingle) {
          return
        }
        const panelYear = this.getCurrentPickerYear() // 获取当前年的。如果是日期范围则需要区分
        // input, type, isParseInputYear, panelYear, maxDate
        const formatValue = parseInputDate({input: this.displayValue, type: this.$props.type, isParseInputYear: this.$props.isParseInputYear, panelYear, maxDate: this.$props.maxDate})
        // this.handleErrorPopoverEvent(!formatValue, this.$refs.reference.$el)
        const value = this.parseString(formatValue)
        const disabled = this.isOptionDisabledDate(value)
        if (disabled) {
          this.userInput = null
          this.pickerVisible = this.picker.visible = false
          this.emitInputEnter({
            value,
            disabled
          })
          return false
        }
        if (value) {
          this.picker.value = value
        }
        this.emitInput(value || null)
        this.emitChange(value || null)
        this.userInput = null
        this.pickerVisible = this.picker.visible = false
        this.blur()
        event.stopPropagation()
        this.emitInputEnter({
          value,
          disabled
        })
        return false
      }
    },
    dateRangeHandleKeydown (event, activeInputIndex) {
      // 单面板的键盘事件
      const keyCode = event.keyCode
      if (keyCode === 13) {
        if (this.pickerDisabled || this.readonlyRanged) {
          return
        }
        const panelYear = this.getCurrentPickerYear() // 获取当前年的。如果是日期范围则需要区分
        const inputDom = this.$refs[`reference${activeInputIndex === 0 ? 'Start' : 'End'}`]
        const inputStr = inputDom.value
        const formatValue = parseInputDate({input: inputStr, type: this.$props.type, isParseInputYear: this.$props.isParseInputYear, panelYear, maxDate: this.$props.maxDate})
        // this.handleErrorPopoverEvent(!formatValue, inputDom)
        const value = this.parseString(formatValue)
        const disabled = this.isOptionDisabledDate(value)
        if (disabled) {
          this.userInput = null
          this.userHover = null
          this.pickerVisible = this.picker.visible = false
          this.emitInputEnter({
            value,
            disabled
          })
          return false
        }
        // console.log(value, this.picker.value, this.parsedValue)
        let data = [...(this.parsedValue || [])]
        data[activeInputIndex] = value
        data = data.map(val => val || '')
        if (data.length !== 2) {
          if (!data[0]) {
            data[0] = ''
          }
          if (!data[1]) {
            data[1] = ''
          }
        }
        if (data.length === 2 && !data[0] && !data[1]) {
          data = []
        }
        if (this.ranged) {
          data = formatAsFormatAndType(data, this.$props.valueFormat, this.$props.type, this.$props.rangeSeparator)
        }
        if (!this.singleValue && (data.some(val => !val))) {
          this.userHover = data
          this.userInput = data
          if (value) {
            this.picker.value = data
          } else {
             this.emitInputEnter({
              value,
              disabled
            })
            return false
          }
          setTimeout(() => {
            this.handleFocus('', activeInputIndex === 0 ? 1 : 0)
            this.$refs[`reference${activeInputIndex === 1 ? 'Start' : 'End'}`].focus()
          }, 250)
          return false
        }
        // 纠正区间
        data = correctDatesOrder(data)
        if (value) {
          this.picker.value = data
        }
        this.userInput = null
        this.userHover = null
        this.emitInput(data || null)
        this.emitChange(data || null)
        this.changStratEendInputBgVisible(false)
        this.blur()
        this.pickerVisible = this.picker.visible = false
        this.emitInputEnter({
          value: data,
          disabled
        })
        event.stopPropagation()
        return false
      }
    },

    handleRangeClick() {
      const type = this.$props.type

      if (HAVE_TRIGGER_TYPES.indexOf(type) !== -1 && !this.pickerVisible) {
        this.pickerVisible = true
      }
      this.$emit('focus', this)
    },

    hidePicker() {
      if (this.picker) {
        this.picker.resetView && this.picker.resetView()
        this.pickerVisible = this.picker.visible = false
        this.destroyPopper()
      }
    },

    showPicker() {
      if (!this.picker) {
        this.mountPicker()
      }
      this.pickerVisible = this.picker.visible = true

      this.updatePopper()

      this.picker.value = this.isDateTimeRange ? [...(this.parsedValue || [])] : this.parsedValue
      if (this.isDoublePanel) {
        this.userHover = this.picker.value
      }
      this.picker.activeInputIndex = this.activeInputIndex
      this.picker.resetView && this.picker.resetView()
      this.picker.type = this.$props.type
      this.picker.singleValue = this.$props.singleValue
      this.picker.showShortcuts = this.$props.showShortcuts
      this.picker.cellClassName = this.$props.cellClassName
      if (this.isDoublePanel && this.picker.value && this.picker.value.length) {
        this.picker.handleInputEnterEvent && this.picker.handleInputEnterEvent(this.picker.value)
      }

      this.$nextTick(() => {
        this.picker.adjustSpinners && this.picker.adjustSpinners()
      })
    },

    mountPicker() {
      this.picker = new Vue(this.panel).$mount()
      this.picker.defaultValue = this.$props.defaultValue
      this.picker.defaultTime = this.$props.defaultTime
      this.picker.popperClass = this.$props.popperClass
      this.popperElm = this.picker.$el
      this.picker.width = this.reference.getBoundingClientRect().width
      this.picker.showTime = (this.$props.type === 'datetime' || this.isDateTimeRange) && this.$props.showTime
      this.picker.selectionMode = this.selectionMode
      this.picker.unlinkPanels = this.$props.unlinkPanels
      this.picker.arrowControl = this.arrowControl || this.timeArrowControl || false
      // 显示农历，并赋值到子组件data数据来控制
      this.picker.showLunar = this.showLunar
      this.picker.handleDateMouseEnter = this.handleDateMouseEnter
      this.picker.handleDateMouseLeave = this.handleDateMouseLeave
      this.picker.type = this.$props.type
      this.picker.showShortcuts = this.$props.showShortcuts
      this.picker.cellClassName = this.$props.cellClassName
      this.$watch('format', (format) => {
        this.picker.format = format
      })

      const updateOptions = () => {
        const options = this.$props.pickerOptions

        if (options && options.selectableRange) {
          let ranges = options.selectableRange
          const parser = TYPE_VALUE_RESOLVER_MAP.datetimerange.parser
          const format = DEFAULT_FORMATS.timerange

          ranges = Array.isArray(ranges) ? ranges : [ranges]
          this.picker.selectableRange = ranges.map(range => parser(range, format, this.$props.rangeSeparator))
        }
        // 最大年份限制
        this.picker.disabledDate = time => time.getTime() > this.$props.maxDate.getTime()

        for (const option in options) {
          if (options.hasOwnProperty(option) &&
              // 忽略 time-picker 的该配置项
              option !== 'selectableRange') {
            this.picker[option] = options[option]
          }
        }

        // 主格式必须优先于未记录的pickerOptions.format
        if (this.$props.format) {
          this.picker.format = this.$props.format
        }
      }
      updateOptions()

      this.unwatchPickerOptions = this.$watch('pickerOptions', () => updateOptions(), { deep: true })
      this.$el.appendChild(this.picker.$el)
      this.picker.resetView && this.picker.resetView()

      this.picker.$on('dodestroy', this.doDestroy)
      this.picker.$on('pick', (date = '', visible = false, openOtherPicker = true) => {
        console.log('pick', date)

        const value = correctDatesOrder(date)
        // 关闭错误的提示。
        // this.handleErrorPopoverEvent(false)
        this.userInput = null
        this.userHover = null
        this.pickerVisible = this.picker.visible = visible
        this.emitInput(value)
        this.picker.resetView && this.picker.resetView()
        if (this.$props.type === 'datetimerange' && openOtherPicker) {
          // 延迟，让其产生动画
          setTimeout(() => {
            let activeInputIndex = -1
            const iter = (val, i) => {
              activeInputIndex = i
              return !val
            }
            if (Array.isArray(value) && value.some(iter)) {
              this.handleFocus('', activeInputIndex)
            }
          }, 250)
        }
        if (this.isDoublePanel) {
          this.daterangeHoverPicks = []
        }
      })
      this.picker.$on('prePick', () => {
        this.handleDateMouseLeave()
      })
      this.picker.$on('reset', () => {
        this.picker.defaultTime = this.$props.defaultTime
        this.$emit('reset')
      })

      this.picker.$on('dateTimeRange', ({ type, newDate }) => {
        this.handleDateMouseEnter({ newDate })
        this.hidePicker()
        if (this.userHover.filter(Boolean).length === 1) {
          setTimeout(() => this.handleFocus('', this.activeInputIndex === 1 ? 0 : 1), 250)
        }
      })

      this.picker.$on('select-range', (start, end, pos) => {
        if (this.refInput.length === 0) return
        if (!pos || pos === 'min') {
          this.refInput[0].setSelectionRange(start, end)
          this.refInput[0].focus()
        } else if (pos === 'max') {
          this.refInput[1].setSelectionRange(start, end)
          this.refInput[1].focus()
        }
      })
    },

    unmountPicker() {
      if (this.picker) {
        this.picker.$destroy()
        this.picker.$off()
        if (typeof this.unwatchPickerOptions === 'function') {
          this.unwatchPickerOptions()
        }
        this.picker.$el.parentNode.removeChild(this.picker.$el)
      }
    },

    emitChange(val) {
      // 仅确定用户实际变化
      if (!valueEquals(val, this.valueOnOpen)) {
        this.$emit('change', val)
        this.valueOnOpen = val
        if (this.$props.validateEvent) {
          this.dispatch('ElFormItem', 'el.form.change', val)
        }
      }
    },

    emitInput(val) {
      const formatted = this.formatToValue(val)
      if (!valueEquals(this.value, formatted)) {
        let value = dateFormatCallback(formatted, this)
        if (this.$props.valueFormatter && typeof this.$props.valueFormatter === 'function') {
          value = this.$props.valueFormatter(val, value)
        }
        this.$emit('input', value)
      }
      this.changStratEendInputBgVisible(false)
    },

    isValidValue(value) {
      if (!this.picker) {
        this.mountPicker()
      }
      if (this.picker.isValidValue) {
        return value && this.picker.isValidValue(value)
      } else {
        return true
      }
    },
    handleDateMouseEnter({ newDate, activeInputIndex, isDateRangePick, val: dateRangeResult = {} } = {}) {
      if (this.isMultiple) {
        return
      }
      if (this.type === 'monthrange') {
        newDate = newDate[0] // 月份的比较特殊。是根据类型来判断的
      }
      if ([0, 1].includes(activeInputIndex)) {
        this.activeInputIndex = activeInputIndex
      }
      if (!this.daterangeHoverPicks) {
       this.daterangeHoverPicks = []
      }
      let datetrangeHover = true
      if (isDateRangePick) {
        if (this.picker.value && this.picker.value.length === 2 && this.picker.value.every(Boolean) && !this.daterangeHoverPicks.length) {
          datetrangeHover = false
          this.userHover = ['', '']
          this.userHover[this.activeInputIndex] = dateRangeResult.minDate || dateRangeResult.maxDate
        }
        this.daterangeHoverPicks[this.activeInputIndex] = true
        if (Array.isArray(this.userHover) && ((this.userHover[1] && !this.userHover[0]) || (!this.userHover[1] && this.userHover[0]))) {
          this.activeInputIndex = this.activeInputIndex === 0 ? 1 : 0
        }
      }

      if (this.ranged) {
        if (this.isDoublePanel) {
          const _value = [...(this.userHover || [])]
          const [s, e] = _value
          if (datetrangeHover) {
            if (isDateRangePick) {
              if (s && !e && this.activeInputIndex === 0) {
                _value[1] = newDate
              } else if (!s && e && this.activeInputIndex === 1) {
                _value[0] = newDate
              }
            } else {
              _value[this.activeInputIndex] = newDate
            }
          }
          this.userHover = [_value[0] || '', _value[1] || '']
        } else {
          const _value = this.$props.singleValue ? [...(this.value || [])] : [...(this.userHover || [])]
          if (this.value && this.value.length) {
            const [l, r] = (Array.isArray(this.value) && this.value) || []
            if (l) _value[0] = l
            if (r) _value[1] = r
          }
          _value[this.activeInputIndex] = newDate
          this.userHover = _value
        }
        // 增加的输入框的bg切换
        if (this.activeInputIndex === 1) {
          this.startInputBgVisible = false
          this.endInputBgVisible = true
        } else {
          this.startInputBgVisible = true
          this.endInputBgVisible = false
        }
      } else {
        this.userHover = newDate
      }
    },
    handleDateMouseLeave() {
      if (this.isMultiple) {
        return
      }
      if (this.isDoublePanel) {
        if (!this.daterangeHoverPicks[this.activeInputIndex] && Array.isArray(this.userHover)) {
          this.userHover.splice(this.activeInputIndex, 1, Array.isArray(this.picker.value) ? this.picker.value[this.activeInputIndex] : '')
        }
      } else {
        if (!this.$props.singleValue && this.type === 'datetimerange') {
          const [l = '', r = ''] = (Array.isArray(this.value) && this.value) || []
          this.userHover = this.activeInputIndex === 1 ? [this.userHover[0] || l, r] : [l, this.userHover[1] || r]
        } else {
          this.userHover = null
        }
      }
    },
    changStratEendInputBgVisible (val, activeInputIndex) {
      if (activeInputIndex !== undefined && String(activeInputIndex)) {
        if (activeInputIndex === 0) {
          this.startInputBgVisible = val
        } else {
          this.endInputBgVisible = val
        }
      } else {
        this.startInputBgVisible = val
        this.endInputBgVisible = val
      }
    },
    handleDeleteMonths () {
      if (this.$props.readonly || this.pickerDisabled) return
      // 删除逻辑
      const value = this.value.slice(0, this.value.length - 1)
      if (this.picker) {
        this.picker.value = this.parsedValue
        this.picker.visible = false
      }
      this.emitInput(value || null)
      this.emitChange(value || null)
      this.userInput = null
      this.pickerVisible = false
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/public/components/kye-date-picker2/src/picker.vue